/**
 * Web-specific Modal wrapper that replaces react-native-modal
 * This avoids React 19 deprecation warnings about element.ref
 * 
 * Provides the same API as react-native-modal but uses web-native approaches
 */

import React, { useEffect, useRef, CSSProperties } from 'react';
import { Modal as RNModal, StyleSheet, Animated, Pressable, View, Easing } from 'react-native';

interface ModalProps {
  isVisible: boolean;
  onBackdropPress?: () => void;
  onBackButtonPress?: () => void;
  onSwipeComplete?: () => void;
  swipeDirection?: 'up' | 'down' | 'left' | 'right' | Array<'up' | 'down' | 'left' | 'right'>;
  backdropColor?: string;
  backdropOpacity?: number;
  backdropTransitionInTiming?: number;
  backdropTransitionOutTiming?: number;
  animationIn?: string | object;
  animationOut?: string | object;
  animationInTiming?: number;
  animationOutTiming?: number;
  avoidKeyboard?: boolean;
  coverScreen?: boolean;
  hasBackdrop?: boolean;
  customBackdrop?: React.ReactNode;
  children?: React.ReactNode;
  deviceHeight?: number;
  deviceWidth?: number;
  hideModalContentWhileAnimating?: boolean;
  propagateSwipe?: boolean;
  statusBarTranslucent?: boolean;
  style?: any;
  useNativeDriver?: boolean;
  useNativeDriverForBackdrop?: boolean;
  scrollTo?: (e: any) => void;
  scrollOffset?: number;
  scrollOffsetMax?: number;
  supportedOrientations?: Array<'portrait' | 'landscape' | 'landscape-left' | 'landscape-right'>;
  onModalWillShow?: () => void;
  onModalShow?: () => void;
  onModalWillHide?: () => void;
  onModalHide?: () => void;
}

const Modal: React.FC<ModalProps> = ({
  isVisible,
  onBackdropPress,
  onBackButtonPress,
  backdropColor = 'black',
  backdropOpacity = 0.7,
  backdropTransitionInTiming = 300,
  backdropTransitionOutTiming = 300,
  animationIn = 'slideInUp',
  animationOut = 'slideOutDown',
  animationInTiming = 300,
  animationOutTiming = 300,
  coverScreen = true,
  hasBackdrop = true,
  customBackdrop,
  children,
  hideModalContentWhileAnimating = false,
  style,
  onModalWillShow,
  onModalShow,
  onModalWillHide,
  onModalHide,
  ...otherProps
}) => {
  const backdropAnim = useRef(new Animated.Value(0)).current;
  const contentAnim = useRef(new Animated.Value(0)).current;
  const [showContent, setShowContent] = React.useState(isVisible);

  useEffect(() => {
    if (isVisible) {
      setShowContent(true);
      onModalWillShow?.();

      // Animate in
      Animated.parallel([
        Animated.timing(backdropAnim, {
          toValue: 1,
          duration: backdropTransitionInTiming,
          useNativeDriver: true,
        }),
        Animated.timing(contentAnim, {
          toValue: 1,
          duration: animationInTiming,
          easing: Easing.out(Easing.ease),
          useNativeDriver: true,
        }),
      ]).start(() => {
        onModalShow?.();
      });
    } else {
      onModalWillHide?.();

      // Animate out
      Animated.parallel([
        Animated.timing(backdropAnim, {
          toValue: 0,
          duration: backdropTransitionOutTiming,
          useNativeDriver: true,
        }),
        Animated.timing(contentAnim, {
          toValue: 0,
          duration: animationOutTiming,
          easing: Easing.in(Easing.ease),
          useNativeDriver: true,
        }),
      ]).start(() => {
        setShowContent(false);
        onModalHide?.();
      });
    }
  }, [isVisible]);

  // Animation transform based on animation type
  const getAnimationStyle = () => {
    const animationType = typeof animationIn === 'string' ? animationIn : 'slideInUp';

    switch (animationType) {
      case 'slideInUp':
      case 'slideOutDown':
        return {
          transform: [{
            translateY: contentAnim.interpolate({
              inputRange: [0, 1],
              outputRange: [600, 0],
            })
          }]
        };
      case 'slideInDown':
      case 'slideOutUp':
        return {
          transform: [{
            translateY: contentAnim.interpolate({
              inputRange: [0, 1],
              outputRange: [-600, 0],
            })
          }]
        };
      case 'slideInLeft':
      case 'slideOutRight':
        return {
          transform: [{
            translateX: contentAnim.interpolate({
              inputRange: [0, 1],
              outputRange: [-600, 0],
            })
          }]
        };
      case 'slideInRight':
      case 'slideOutLeft':
        return {
          transform: [{
            translateX: contentAnim.interpolate({
              inputRange: [0, 1],
              outputRange: [600, 0],
            })
          }]
        };
      case 'fadeIn':
      case 'fadeOut':
        return {
          opacity: contentAnim,
        };
      case 'zoomIn':
      case 'zoomOut':
        return {
          transform: [{
            scale: contentAnim.interpolate({
              inputRange: [0, 1],
              outputRange: [0, 1],
            })
          }],
          opacity: contentAnim,
        };
      default:
        return {
          opacity: contentAnim,
        };
    }
  };

  if (!showContent && !isVisible) {
    return null;
  }

  // Extract z-index from style prop if provided, otherwise use default
  const styleZIndex = style?.zIndex || 10;
  const backdropZIndex = styleZIndex - 1;

  return (
    <RNModal
      transparent
      visible={showContent}
      onRequestClose={onBackButtonPress || onBackdropPress}
      {...otherProps}
    >
      <View style={[styles.container, coverScreen && styles.coverScreen]}>
        {/* Content - render first with pointer-events-none to let backdrop catch clicks */}
        <Animated.View
          pointerEvents="box-none"
          style={[
            styles.content,
            getAnimationStyle(),
            style,
            {
              zIndex: styleZIndex,
              pointerEvents: 'box-none',
            },
          ]}
        >
          <View pointerEvents="auto">
            {(!hideModalContentWhileAnimating || isVisible) && children}
          </View>
        </Animated.View>

        {/* Backdrop - render after content but with z-index just below content */}
        {hasBackdrop && onBackdropPress && (
          <Pressable onPress={onBackdropPress}>
            <Animated.View
              style={[
                {
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: backdropZIndex,
                  backgroundColor: backdropOpacity === 0 ? 'transparent' : backdropColor,
                  opacity: backdropAnim.interpolate({
                    inputRange: [0, 1],
                    outputRange: [0, backdropOpacity],
                  }),
                },
              ]}
            />
          </Pressable>
        )}

        {/* Custom Backdrop */}
        {customBackdrop}
      </View>
    </RNModal>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  coverScreen: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  backdrop: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  content: {
    flex: 1,
  },
});

export default Modal;


